<template>
  <div class="incident-ticker" v-if="visible">
    <!-- Cap fija a la izquierda: solo el icono (ancla el banner, no se desplaza) -->
    <div class="incident-ticker__cap">
      <v-icon class="incident-ticker__cap-icon">mdi-bullhorn-outline</v-icon>
    </div>

    <!-- Estado vacío: visible pero sin incidentes activos -->
    <div v-if="!incidents.length" class="incident-ticker__empty">
      Sin incidentes activos
    </div>

    <!-- Track con movimiento manejado por requestAnimationFrame (no CSS
         animation): velocidad constante en px/s. Dos grupos idénticos; el
         offset se envuelve al pasar el ancho de un grupo → loop sin costura.
         Pausa en hover/focus. Los items son <div> (un <button> no hereda la
         tipografía del documento y forzaba la fuente del navegador). -->
    <div
      v-else
      class="incident-ticker__viewport"
      @mouseenter="_paused = true"
      @mouseleave="_paused = false"
      @focusin="_paused = true"
      @focusout="_paused = false"
    >
      <div class="incident-ticker__track" ref="track">
        <div class="incident-ticker__group" ref="groupA">
          <div
            v-for="item in incidents"
            :key="'a-' + item.monitorId"
            class="ticker-item"
            role="button"
            tabindex="0"
            :style="{ background: item.prioridadColor }"
            v-auto-tooltip="item.tipo"
            @click="$emit('select', item.monitorId)"
            @keydown.enter="$emit('select', item.monitorId)"
            @keydown.space.prevent="$emit('select', item.monitorId)"
          >
            <span class="ti-left">
              <span class="ti-cat" :style="{ background: item.catColor }">
                <v-icon class="ti-cat__icon">{{ item.catIcon }}</v-icon>
                <span class="ti-cat__label">{{ item.categoria }}</span>
              </span>
              <span v-if="item.prioridad" class="ti-prio" :style="{ color: item.prioridadColor }">{{ item.prioridad }}</span>
            </span>
            <span class="ti-title">{{ item.tipo }}</span>
            <span class="ti-right">
              <span v-if="item.lugar" class="ti-place">{{ item.lugar }}</span>
              <span class="ti-time">{{ item.hora }}</span>
            </span>
          </div>
        </div>
        <!-- Copia para el loop; oculta a lectores de pantalla -->
        <div class="incident-ticker__group" aria-hidden="true">
          <div
            v-for="item in incidents"
            :key="'b-' + item.monitorId"
            class="ticker-item"
            :style="{ background: item.prioridadColor }"
            @click="$emit('select', item.monitorId)"
          >
            <span class="ti-left">
              <span class="ti-cat" :style="{ background: item.catColor }">
                <v-icon class="ti-cat__icon">{{ item.catIcon }}</v-icon>
                <span class="ti-cat__label">{{ item.categoria }}</span>
              </span>
              <span v-if="item.prioridad" class="ti-prio" :style="{ color: item.prioridadColor }">{{ item.prioridad }}</span>
            </span>
            <span class="ti-title">{{ item.tipo }}</span>
            <span class="ti-right">
              <span v-if="item.lugar" class="ti-place">{{ item.lugar }}</span>
              <span class="ti-time">{{ item.hora }}</span>
            </span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
// Velocidad de desplazamiento en píxeles por segundo. Constante → el scroll
// se ve uniforme sin importar cuántos incidentes haya (a diferencia de la
// animación CSS atada a la duración, que saltaba al cambiar el feed).
var SPEED_PX_SEC = 32

export default {
  name: 'IncidentTicker',
  props: {
    // Lista ya ordenada/capada por MapV2. Cada item:
    // { monitorId, tipo, categoria, catIcon, catColor, lugar, hora,
    //   prioridad, prioridadColor }
    incidents: { type: Array, default: () => [] },
    visible: { type: Boolean, default: false },
  },
  created() {
    // Estado del motor de scroll — NO reactivo (se lee/escribe en cada frame).
    this._raf = null
    this._lastTs = 0
    this._offset = 0
    this._groupWidth = 0
    this._paused = false
    this._boundTick = this._tick.bind(this)
  },
  mounted() {
    if (this.visible && this.incidents.length) this.$nextTick(this._start)
  },
  beforeDestroy() {
    this._stop()
  },
  watch: {
    visible(v) {
      if (v && this.incidents.length) this.$nextTick(this._start)
      else this._stop()
    },
    incidents(list) {
      // El contenido cambió (entró/salió un incidente, o cambió un texto):
      // invalidamos el ancho medido para re-medir en el próximo frame, PERO
      // conservamos this._offset → el scroll continúa sin saltar.
      this._groupWidth = 0
      if (this.visible && list.length) {
        if (!this._raf) this.$nextTick(this._start)
      } else if (!list.length) {
        this._stop()
      }
    },
  },
  methods: {
    _start() {
      if (this._raf) return
      this._lastTs = 0
      this._raf = requestAnimationFrame(this._boundTick)
    },
    _stop() {
      if (this._raf) {
        cancelAnimationFrame(this._raf)
        this._raf = null
      }
      this._lastTs = 0
    },
    _measure() {
      var g = this.$refs.groupA
      this._groupWidth = g ? g.offsetWidth : 0
    },
    _tick(ts) {
      this._raf = requestAnimationFrame(this._boundTick)
      var track = this.$refs.track
      if (!track) return
      // Primer frame: solo fijamos la referencia temporal (dt=0).
      if (!this._lastTs) { this._lastTs = ts; return }
      var dt = (ts - this._lastTs) / 1000
      this._lastTs = ts
      // Clamp: al volver de una pestaña en segundo plano dt puede ser enorme.
      if (dt > 0.1) dt = 0.1
      if (this._paused) return
      if (!this._groupWidth) this._measure()
      var gw = this._groupWidth
      if (gw <= 0) return
      this._offset += SPEED_PX_SEC * dt
      // Envolver al ancho de un grupo (el segundo grupo cubre la costura).
      while (this._offset >= gw) this._offset -= gw
      track.style.transform = 'translateX(' + (-this._offset) + 'px)'
    },
  },
}
</script>

<style scoped>
/* Banner de ALERTA para VIDEO WALL. Cada item se pinta con el COLOR DE SU
   PRIORIDAD (tokens del sidebar). Tipografía = la misma de la app (Public
   Sans vía --kln-font-family), forzada en el root. */
.incident-ticker {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: stretch;
  background: var(--kln-alert-danger);
  color: #fff;
  overflow: hidden;
  font-family: var(--kln-font-family);
  /* Altura compartida de los bloques de 2 líneas (chips izq. / lugar+tiempo
     der.). Se fija al alto NATURAL del par de texto (TOLUCA + hace) para no
     estirarlo; los chips se encogen para caber en esta altura. */
  --ti-block-h: 26px;
}

/* Cap fija (solo icono) a la izquierda — no se desplaza */
.incident-ticker__cap {
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 16px;
  background: rgb(163, 45, 45);
  border-right: 2px solid rgba(255, 255, 255, 0.4);
}

.incident-ticker__cap-icon {
  color: #fff !important;
  font-size: 24px !important;
}

.incident-ticker__empty {
  display: flex;
  align-items: center;
  width: 100%;
  padding-left: 22px;
  font-size: 16px;
  font-weight: 700;
  letter-spacing: 0.04em;
  color: #fff;
}

.incident-ticker__viewport {
  flex: 1 1 auto;
  height: 100%;
  overflow: hidden;
}

.incident-ticker__track {
  display: flex;
  width: max-content;
  height: 100%;
  will-change: transform;
}

.incident-ticker__group {
  display: flex;
  align-items: stretch;
  height: 100%;
}

.ticker-item {
  display: inline-flex;
  align-items: center;
  gap: 14px;
  height: 100%;
  padding: 0 20px;
  white-space: nowrap;
  border-right: 2px solid rgba(255, 255, 255, 0.22);
  cursor: pointer;
  color: #fff;
  outline: none;
}

.ticker-item:hover,
.ticker-item:focus-visible {
  filter: brightness(1.12);
}

/* ── Bloque izquierdo: categoría (arriba) + prioridad (abajo) ──
   Misma altura (--ti-block-h) y space-between que el bloque derecho, para que
   SEGURIDAD↔TOLUCA (arriba) y ALTA↔hace (abajo) queden alineados.
   align-items: stretch iguala el ancho de ambos chips al más ancho. */
.ti-left {
  display: flex;
  flex-direction: column;
  align-items: stretch;
  justify-content: space-between;
  height: var(--ti-block-h, 26px);
  box-sizing: border-box;
}

/* Chips pequeños, mismo aire que los chips de la toolbar. Texto centrado. */
.ti-cat {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  padding: 1px 8px;
  border-radius: 4px;
  color: #fff;
  box-sizing: border-box;
}

.ti-cat__icon {
  color: #fff !important;
  font-size: 11px !important;
}

.ti-cat__label {
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.03em;
  text-transform: uppercase;
  line-height: 1;
}

.ti-prio {
  display: block;
  text-align: center;
  font-size: 9px;
  font-weight: 800;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  padding: 1px 7px;
  border-radius: 4px;
  background: #fff;
  line-height: 1.1;
  box-sizing: border-box;
}

/* ── Título: el dato más prominente, siempre 1 línea ── */
.ti-title {
  font-size: 16px;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0.02em;
  line-height: 1.1;
  color: #fff;
}

/* ── Bloque derecho: lugar (arriba) + tiempo (abajo) ──
   Misma altura (--ti-block-h) y space-between que el bloque izquierdo:
   TOLUCA queda alineado con SEGURIDAD (arriba) y "hace 5h" con ALTA (abajo). */
.ti-right {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: space-between;
  height: var(--ti-block-h, 26px);
  box-sizing: border-box;
}

.ti-place {
  font-size: 12px;
  font-weight: 700;
  line-height: 1.1;
  color: #fff;
}

.ti-time {
  font-size: 10px;
  font-weight: 600;
  line-height: 1.1;
  color: rgba(255, 255, 255, 0.85);
  font-variant-numeric: tabular-nums;
}
</style>
